// Real-time collaboration using Supabase Realtime (up to 4 users)
import { supabase } from './supabase';

let currentChannel = null;
let presenceInterval = null;
let currentUserName = null;

export function joinAuditSession(auditId, userId, userName, onUpdate, onPresenceChange, onToast) {
  leaveAuditSession();
  currentUserName = userName;

  const channelName = `audit:${auditId}`;
  currentChannel = supabase.channel(channelName, {
    config: { presence: { key: userId || 'anonymous' } },
  });

  // Track who's online
  currentChannel.on('presence', { event: 'sync' }, () => {
    const state = currentChannel.presenceState();
    const users = Object.entries(state).map(([key, presences]) => ({
      id: key,
      name: presences[0]?.name || 'Unknown',
      lastSeen: presences[0]?.lastSeen || new Date().toISOString(),
      editing: presences[0]?.editing || null,
    }));
    if (onPresenceChange) onPresenceChange(users);
  });

  // Track joins
  currentChannel.on('presence', { event: 'join' }, ({ key, newPresences }) => {
    const name = newPresences[0]?.name;
    if (name && key !== userId && onToast) {
      onToast(`${name} joined the audit`);
    }
  });

  // Track leaves
  currentChannel.on('presence', { event: 'leave' }, ({ key, leftPresences }) => {
    const name = leftPresences[0]?.name;
    if (name && key !== userId && onToast) {
      onToast(`${name} left the audit`);
    }
  });

  // Listen for audit data changes
  currentChannel.on('broadcast', { event: 'audit_update' }, (payload) => {
    if (payload.payload?.userId !== userId) {
      if (onUpdate) onUpdate(payload.payload.audit, payload.payload.userName);
      if (onToast && payload.payload.field) {
        onToast(`${payload.payload.userName} updated ${payload.payload.field}`);
      }
    }
  });

  // Subscribe and track presence
  currentChannel.subscribe(async (status) => {
    if (status === 'SUBSCRIBED') {
      await currentChannel.track({
        name: userName || 'Unknown',
        lastSeen: new Date().toISOString(),
        editing: null,
      });

      presenceInterval = setInterval(async () => {
        try {
          await currentChannel.track({
            name: userName || 'Unknown',
            lastSeen: new Date().toISOString(),
          });
        } catch (e) {}
      }, 30000);
    }
  });

  return currentChannel;
}

// Broadcast an audit update to other collaborators
// Only send the lightweight/changed parts to avoid Supabase message size limits
export function broadcastUpdate(audit, userId, userName, field = null) {
  if (!currentChannel) return;
  // Send only scores, key fields, and metadata — not photos, AI research, etc.
  const lightweight = {
    id: audit.id,
    scores: audit.scores,
    memoryTest: audit.memoryTest,
    socialProof: audit.socialProof,
    brandCoherence: audit.brandCoherence,
    revenueLeaks: audit.revenueLeaks,
    menuAnalysis: audit.menuAnalysis,
    beverageAnalysis: audit.beverageAnalysis,
    comments: audit.comments,
    teamScores: audit.teamScores,
    roadmapProgress: audit.roadmapProgress,
    roadmapComments: audit.roadmapComments,
    approvedCompetitors: audit.approvedCompetitors,
    marketData: audit.marketData ? { competitors: audit.marketData.competitors } : null,
  };
  try {
    currentChannel.send({
      type: 'broadcast',
      event: 'audit_update',
      payload: { audit: lightweight, userId, userName: userName || currentUserName || 'Team', field },
    });
  } catch (e) {
    console.warn('Broadcast failed:', e.message);
  }
}

// Update what you're currently editing
export async function broadcastEditing(field) {
  if (!currentChannel) return;
  try {
    await currentChannel.track({
      name: currentUserName || 'Unknown',
      editing: field,
      lastSeen: new Date().toISOString(),
    });
    // Clear editing indicator after 5 seconds
    setTimeout(async () => {
      try {
        await currentChannel.track({
          name: currentUserName || 'Unknown',
          editing: null,
          lastSeen: new Date().toISOString(),
        });
      } catch (e) {}
    }, 5000);
  } catch (e) {}
}

// Leave the session
export function leaveAuditSession() {
  if (presenceInterval) {
    clearInterval(presenceInterval);
    presenceInterval = null;
  }
  if (currentChannel) {
    currentChannel.unsubscribe();
    currentChannel = null;
  }
  currentUserName = null;
}
